import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { ScanInput } from '@/modules/wms/components/common/ScanInput';
import { CameraScanner } from '@/shared/components/scanner/CameraScanner';
import { productsAPI, wmsAPI } from '@/shared/services/api';
import {
  ScanLine,
  Package,
  MapPin,
  Barcode,
  History,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Thermometer,
  Camera,
  Keyboard } from
'lucide-react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger } from
'@/shared/components/ui/tabs';
import { toast } from 'sonner';

export const PharmaScan = () => {const { t } = useTranslation("wms");
  const [scannedItem, setScannedItem] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [products, setProducts] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [scanMode, setScanMode] = useState('manual'); // 'manual' or 'camera'

  // Fetch products and inventory on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsData, inventoryData] = await Promise.all([
        productsAPI.getProducts(),
        wmsAPI.getInventory()]
        );
        setProducts(productsData || []);
        setInventory(inventoryData || []);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };
    fetchData();
  }, []);

  const handleScan = async (barcode) => {
    // Find product by barcode or SKU
    const product = products.find((p) => p.barcode === barcode || p.sku === barcode);
    const invItem = inventory.find((i) => i.sku === barcode);

    if (product || invItem) {
      const result = {
        type: 'success',
        barcode,
        product: product || { name: invItem?.product_name || invItem?.productName, sku: invItem?.sku },
        inventory: invItem,
        timestamp: new Date().toISOString()
      };
      setScannedItem(result);
      setScanHistory((prev) => [result, ...prev].slice(0, 10));
      toast.success('Item Found', { description: `${product?.name || invItem?.product_name || barcode}` });
    } else {
      const result = {
        type: 'error',
        barcode,
        message: 'Item not found',
        timestamp: new Date().toISOString()
      };
      setScannedItem(result);
      setScanHistory((prev) => [result, ...prev].slice(0, 10));
      toast.error('Item Not Found', { description: `No product found for: ${barcode}` });
    }
  };

  // Demo scan for testing
  const handleDemoScan = () => {
    const demoSku = products.length > 0 ? products[0].sku : 'SKU-A1001';
    handleScan(demoSku);
  };

  return (
    <div className="space-y-6" data-testid="wms-pharmascan">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("pharmascan", "PharmaScan")}</h1>
          <p className="text-muted-foreground mt-1">{t("quickBarcodeScanning", "Quick barcode scanning for inventory lookup and verification")}

          </p>
        </div>
        <Button variant="outline" onClick={handleDemoScan} className="gap-2">
          <ScanLine className="h-4 w-4" />{t("demoScan", "Demo Scan")}

        </Button>
      </div>

      {/* Main Scan Area */}
      <Card className="card-elevated overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
          <div className="max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 mb-4">
              <ScanLine className="h-8 w-8" />
            </div>
            <h2 className="text-2xl font-bold mb-2">{t("readyToScan", "Ready to Scan")}</h2>
            <p className="text-blue-100 mb-4">{t("useCameraOr", "Use camera or enter barcode manually")}

            </p>
            
            {/* Scan Mode Tabs */}
            <Tabs value={scanMode} onValueChange={setScanMode} className="max-w-md mx-auto">
              <TabsList className="grid w-full grid-cols-2 bg-white/20">
                <TabsTrigger value="manual" className="gap-2 data-[state=active]:bg-white data-[state=active]:text-blue-600">
                  <Keyboard className="h-4 w-4" />{t("manualEntry", "Manual Entry")}

                </TabsTrigger>
                <TabsTrigger value="camera" className="gap-2 data-[state=active]:bg-white data-[state=active]:text-blue-600">
                  <Camera className="h-4 w-4" />{t("cameraScan", "Camera Scan")}

                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
        
        <CardContent className="p-6">
          {scanMode === 'manual' ?
          <div className="max-w-lg mx-auto">
              <ScanInput
              placeholder="Scan barcode or enter SKU / product code..."
              onScan={handleScan} />
            
            </div> :

          <div className="max-w-lg mx-auto">
              <CameraScanner
              onScan={(barcode) => handleScan(barcode)}
              showControls={true}
              autoStart={false} />
            
              <p className="text-xs text-center text-muted-foreground mt-3">{t("pointYourDevice", "Point your device camera at a barcode to scan")}

            </p>
            </div>
          }

        {/* Scan Result */}
        {scannedItem &&
          <div className="mt-6 pt-6 border-t">
            {scannedItem.type === 'success' ?
            <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-green-100 dark:bg-green-900/50">
                    <CheckCircle2 className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{t("itemFound", "Item Found")}</h3>
                    <p className="text-sm text-muted-foreground">{t("scanned", "Scanned:")}
                    {scannedItem.barcode}
                    </p>
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  {/* Product Info */}
                  <div className="p-4 rounded-lg border border-border bg-muted/30">
                    <div className="flex items-center gap-2 mb-3">
                      <Package className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium text-muted-foreground">{t("productInformation", "Product Information")}</span>
                    </div>
                    <h4 className="font-semibold mb-2">{scannedItem.product?.name}</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t("sku", "SKU:")}</span>
                        <span className="font-mono">{scannedItem.product?.sku}</span>
                      </div>
                      {scannedItem.inventory &&
                    <>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">{t("category", "Category:")}</span>
                            <span>{scannedItem.inventory.category}</span>
                          </div>
                        </>
                    }
                    </div>
                  </div>

                  {/* Inventory Info */}
                  {scannedItem.inventory &&
                <div className="p-4 rounded-lg border border-border bg-muted/30">
                      <div className="flex items-center gap-2 mb-3">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium text-muted-foreground">{t("inventoryStatus", "Inventory Status")}</span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{t("location", "Location:")}</span>
                          <span className="font-mono">{scannedItem.inventory.location || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{t("zone", "Zone:")}</span>
                          <div className="flex items-center gap-1">
                            {scannedItem.inventory.zone?.includes('Cold') &&
                        <Thermometer className="h-3.5 w-3.5 text-blue-500" />
                        }
                            <span>{scannedItem.inventory.zone || 'N/A'}</span>
                          </div>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{t("quantity", "Quantity:")}</span>
                          <span className="font-semibold">{scannedItem.inventory.quantity ?? 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{t("available", "Available:")}</span>
                          <span className={(scannedItem.inventory.availableQty ?? 0) < (scannedItem.inventory.reorderLevel ?? 0) ? 'text-red-600 font-semibold' : 'font-semibold'}>
                            {scannedItem.inventory.availableQty ?? 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{t("lot", "Lot #:")}</span>
                          <span className="font-mono">{scannedItem.inventory.lotNumber || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{t("expiry", "Expiry:")}</span>
                          <span>{scannedItem.inventory.expiryDate || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                }
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" className="gap-2">
                    <MapPin className="h-4 w-4" />{t("moveLocation", "Move Location")}

                </Button>
                  <Button variant="outline" className="gap-2">
                    <Package className="h-4 w-4" />{t("adjustStock", "Adjust Stock")}

                </Button>
                  <Button variant="outline" className="gap-2">
                    <History className="h-4 w-4" />{t("viewHistory", "View History")}

                </Button>
                </div>
              </div> :

            <div className="flex items-center gap-3 p-4 rounded-lg bg-red-50 dark:bg-red-950/20">
                <div className="p-2 rounded-full bg-red-100 dark:bg-red-900/50">
                  <XCircle className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-red-700 dark:text-red-400">{t("itemNotFound", "Item Not Found")}</h3>
                  <p className="text-sm text-muted-foreground">{t("noProductFound", "No product found for barcode:")}
                  {scannedItem.barcode}
                  </p>
                </div>
              </div>
            }
          </div>
          }
        </CardContent>
      </Card>

      {/* Scan History */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <History className="h-5 w-5 text-muted-foreground" />{t("recentScans", "Recent Scans")}

          </CardTitle>
        </CardHeader>
        <CardContent>
          {scanHistory.length > 0 ?
          <div className="space-y-2">
              {scanHistory.map((scan, index) =>
            <div
              key={index}
              className={`flex items-center justify-between p-3 rounded-lg border ${
              scan.type === 'success' ?
              'border-green-200 bg-green-50/50 dark:bg-green-950/20' :
              'border-red-200 bg-red-50/50 dark:bg-red-950/20'}`
              }>
              
                  <div className="flex items-center gap-3">
                    {scan.type === 'success' ?
                <CheckCircle2 className="h-5 w-5 text-green-600" /> :

                <XCircle className="h-5 w-5 text-red-600" />
                }
                    <div>
                      <p className="font-mono text-sm font-medium">{scan.barcode}</p>
                      <p className="text-xs text-muted-foreground">
                        {scan.type === 'success' ? scan.product?.name : scan.message}
                      </p>
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {new Date(scan.timestamp).toLocaleTimeString()}
                  </span>
                </div>
            )}
            </div> :

          <div className="text-center py-8">
              <Barcode className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
              <p className="text-sm text-muted-foreground">{t("noScansYet", "No scans yet")}</p>
              <p className="text-xs text-muted-foreground mt-1">{t("scanABarcode", "Scan a barcode to see history")}

            </p>
            </div>
          }
        </CardContent>
      </Card>

      {/* Quick Tips */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="text-lg">{t("scanningTips", "Scanning Tips")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/50">
                <ScanLine className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <h4 className="font-medium text-sm">{t("positionCorrectly", "Position Correctly")}</h4>
                <p className="text-xs text-muted-foreground">{t("holdBarcode46", "Hold barcode 4-6 inches from scanner")}

                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/50">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <h4 className="font-medium text-sm">{t("waitForBeep", "Wait for Beep")}</h4>
                <p className="text-xs text-muted-foreground">{t("confirmationSoundIndicates", "Confirmation sound indicates successful scan")}

                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/50">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
              </div>
              <div>
                <h4 className="font-medium text-sm">{t("cleanBarcodes", "Clean Barcodes")}</h4>
                <p className="text-xs text-muted-foreground">{t("removeDirtOr", "Remove dirt or smudges for better reads")}

                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>);

};